import { useCallback, useEffect } from 'react';
import { useAuth } from '../../../modules/auth';
import useSocket from '../../../hooks/useSocket';
import {
  getPendingVerificationCodes,
  getUnitConsumerVerificationCode,
  UnitConsumerVerificationCode,
} from '../../../services/unitConsumerService';
import { useAlertToast } from './index';
import {
  CachedVerificationCode,
  dismissCode,
  expiresAtFrom,
  getActiveCodes,
  isDismissed,
  saveActiveCode,
  verificationCodeKey,
} from './verificationCodeCache';

const capitalize = (value?: string | null) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

/**
 * Escuta o canal "verificationCode" do websocket e empilha os códigos de
 * verificação da concessionária junto dos alertas.
 *
 * O socket só avisa que chegou um código (sem o código); o código vem da API.
 * Para não perder códigos que chegaram com o usuário off-line, busca os
 * pendentes no backend ao abrir a página e a cada reconexão do socket, e
 * restaura do cache local os que já estavam na tela.
 */
const VerificationCodeListener = () => {
  const { currentUser } = useAuth();
  const { socket, on, off } = useSocket();
  const { pushAlert } = useAlertToast();
  const userId = currentUser && currentUser.id ? currentUser.id : null;

  const showCode = useCallback(
    (item: CachedVerificationCode, playSound: boolean) => {
      if (!userId) return;

      const brand = capitalize(item.brand);
      pushAlert({
        type: 'VERIFICATION_CODE',
        title: `Código de verificação${brand ? ` ${brand}` : ''}`,
        description: 'Use este código para concluir o cadastro na concessionária.',
        code: item.code,
        brand: item.brand,
        idUnitConsumer: item.idUnitConsumer,
        unitConsumerName: item.unitConsumerName,
        createdAt: item.receivedAt,
        expiresAt: item.expiresAt,
        dedupeKey: item.key,
        playSound,
        onDismiss: () => dismissCode(userId, item.key, item.expiresAt),
      });
    },
    [userId, pushAlert]
  );

  const handleResult = useCallback(
    (result: UnitConsumerVerificationCode) => {
      if (!userId || !result || !result.code || !result.receivedAt) return;

      const key = verificationCodeKey(result.idUnitConsumer, result.receivedAt);
      if (isDismissed(userId, key)) return;

      const expiresAt = expiresAtFrom(result.receivedAt);
      if (new Date(expiresAt).getTime() <= Date.now()) return;

      const permissions = currentUser && currentUser.permissions ? currentUser.permissions : null;
      const soundEnabled = !permissions || permissions.notification_alert_sound !== false;
      const alreadyCached = getActiveCodes(userId).some((item) => item.key === key);

      const item: CachedVerificationCode = {
        key,
        code: result.code,
        brand: result.brand,
        idUnitConsumer: result.idUnitConsumer,
        unitConsumerName: result.unitConsumerName,
        receivedAt: result.receivedAt,
        expiresAt,
      };

      saveActiveCode(userId, item);
      showCode(item, soundEnabled && !alreadyCached);
    },
    [userId, currentUser, showCode]
  );

  const fetchPending = useCallback(async () => {
    try {
      const pending = await getPendingVerificationCodes();
      (pending || []).forEach(handleResult);
    } catch (error) {
      console.error('Erro ao buscar códigos de verificação pendentes:', error);
    }
  }, [handleResult]);

  // Restaura do cache e busca os que chegaram com o usuário off-line
  useEffect(() => {
    if (!userId) return;

    getActiveCodes(userId)
      .slice()
      .reverse()
      .forEach((item) => showCode(item, false));

    fetchPending();
  }, [userId, showCode, fetchPending]);

  useEffect(() => {
    const handleVerificationCode = async (data: any) => {
      if (!data || !data.idUnitConsumer) return;
      try {
        let result = await getUnitConsumerVerificationCode(data.idUnitConsumer);
        // Aviso chegou mas o código ainda não está disponível (ex.: diferença de
        // relógio entre servidores): tenta mais uma vez antes de desistir.
        if (!result || !result.code) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          result = await getUnitConsumerVerificationCode(data.idUnitConsumer);
        }
        handleResult(result);
      } catch (error) {
        console.error('Erro ao buscar código de verificação:', error);
      }
    };

    const handleReconnect = () => fetchPending();

    if (userId && socket) {
      on('verificationCode', handleVerificationCode);
      // Reconexão: busca o que chegou enquanto o socket estava caído. Direto no
      // socket porque off() do hook remove todos os handlers do evento, inclusive
      // o "connect" interno que registra o usuário na sala.
      socket.on('connect', handleReconnect);
    }

    return () => {
      if (socket) {
        off('verificationCode', handleVerificationCode);
        socket.off('connect', handleReconnect);
      }
    };
  }, [userId, socket, on, off, handleResult, fetchPending]);

  return null;
};

export default VerificationCodeListener;
